import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { NativeSelect } from '../../components/ui/native-select';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, 
  Clock, GraduationCap, Users, Download, Filter, 
  Edit, Trash2, X, BookOpen, Building2, Monitor, 
  FileText, CheckCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

// Event type configuration
const EVENT_TYPES = {
  cours: { label: "Cours", color: "bg-violet-500", icon: BookOpen },
  entreprise: { label: "Entreprise", color: "bg-amber-500", icon: Building2 },
  cours_en_ligne: { label: "Cours en ligne", color: "bg-blue-500", icon: Monitor },
  cours_physique: { label: "Cours physique", color: "bg-emerald-500", icon: GraduationCap },
  examen: { label: "Examen", color: "bg-red-500", icon: FileText },
  revision: { label: "Révision", color: "bg-orange-500", icon: RefreshCw },
  devoir: { label: "Devoir", color: "bg-pink-500", icon: CheckCircle }
};

const EVENT_TYPE_OPTIONS = Object.entries(EVENT_TYPES).map(([value, config]) => ({
  value,
  label: config.label
}));

/** Local YYYY-MM-DD — never use toISOString() for calendar days (UTC shift). */
const toLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const CfaAdminPlanningPage = ({ onMenuClick }) => {
  // Data states
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Filter states
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'cours',
    event_date: '',
    start_time: '09:00',
    end_time: '12:00',
    group_id: '',
    student_id: '',
    course_id: '',
    trainer_id: '',
    location: '',
    resources_url: ''
  });

  // Batch creation state
  const [batchDates, setBatchDates] = useState([]);
  const [batchMonth, setBatchMonth] = useState(new Date().getMonth());
  const [batchYear, setBatchYear] = useState(new Date().getFullYear());

  // Recurrence state (e.g. every Monday between two dates)
  const [recurStart, setRecurStart] = useState('');
  const [recurEnd, setRecurEnd] = useState('');
  const [recurWeekdays, setRecurWeekdays] = useState([]); // 1=Mon ... 0=Sun

  const toggleRecurWeekday = (wd) => {
    setRecurWeekdays(prev =>
      prev.includes(wd) ? prev.filter(d => d !== wd) : [...prev, wd]
    );
  };

  // Generate all matching dates between start and end for the chosen weekdays
  const generateRecurringDates = () => {
    if (!recurStart || !recurEnd) {
      toast.error('Veuillez choisir une date de début et de fin');
      return;
    }
    if (recurWeekdays.length === 0) {
      toast.error('Veuillez choisir au moins un jour de la semaine');
      return;
    }
    const start = new Date(recurStart);
    const end = new Date(recurEnd);
    if (start > end) {
      toast.error('La date de début doit précéder la date de fin');
      return;
    }
    const generated = [];
    const cursor = new Date(start);
    let guard = 0;
    while (cursor <= end && guard < 1000) {
      if (recurWeekdays.includes(cursor.getDay())) {
        const y = cursor.getFullYear();
        const m = String(cursor.getMonth() + 1).padStart(2, '0');
        const d = String(cursor.getDate()).padStart(2, '0');
        generated.push(`${y}-${m}-${d}`);
      }
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
    setBatchDates(prev => Array.from(new Set([...prev, ...generated])));
    toast.success(`${generated.length} date(s) ajoutée(s)`);
  };
  
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportView, setExportView] = useState('year');
  const [exportTarget, setExportTarget] = useState('group');
  const [exportId, setExportId] = useState('');

  // Fetch all data
  const fetchData = useCallback(async () => {
    // Load each resource independently so one failing endpoint never empties the
    // Groupe / Étudiant dropdowns (previous Promise.all was all-or-nothing).
    try {
      const [eventsRes, groupsRes, studentsRes, coursesRes, trainersRes] = await Promise.all([
        api.get('/planning/events').catch(() => ({ data: [] })),
        api.get('/groups')
          .catch(() => api.get('/documents/groups'))
          .catch((err) => {
            console.error('Erreur chargement groupes:', err);
            return { data: [] };
          }),
        api.get('/students')
          .catch(() => api.get('/documents/students'))
          .catch((err) => {
            console.error('Erreur chargement étudiants:', err);
            return { data: [] };
          }),
        api.get('/courses').catch(() => ({ data: [] })),
        api.get('/trainers').catch(() => ({ data: [] }))
      ]);

      const toArray = (data, ...keys) => {
        if (Array.isArray(data)) return data;
        for (const key of keys) {
          if (Array.isArray(data?.[key])) return data[key];
        }
        return [];
      };

      setEvents(toArray(eventsRes.data, 'items', 'events'));
      setGroups(toArray(groupsRes.data, 'items', 'groups'));
      setStudents(toArray(studentsRes.data, 'items', 'students'));
      setCourses(toArray(coursesRes.data, 'items', 'courses'));
      setTrainers(toArray(trainersRes.data, 'items', 'trainers'));
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calendar navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    // Start from Monday before first day
    startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
    
    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (7 - endDate.getDay()) % 7);
    
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Get events for a specific day
  const getEventsForDay = (day) => {
    const dayStr = toLocalDateKey(day);
    return events.filter(e => {
      const matches = e.event_date === dayStr;
      if (!matches) return false;
      if (filterGroup !== 'all' && e.group_id?.toString() !== filterGroup) return false;
      if (filterType !== 'all' && e.event_type !== filterType) return false;
      return true;
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'cours',
      event_date: selectedDate ? toLocalDateKey(selectedDate) : '',
      start_time: '09:00',
      end_time: '12:00',
      group_id: '',
      student_id: '',
      course_id: '',
      trainer_id: '',
      location: '',
      resources_url: ''
    });
  };

  // Open create modal
  const openCreateModal = (date = null) => {
    setEditingEvent(null);
    setSelectedDate(date);
    resetForm();
    if (date) {
      setFormData(prev => ({ ...prev, event_date: toLocalDateKey(date) }));
    }
    setShowCreateModal(true);
    // Refresh groups/students/courses/trainers so the dropdowns are never empty.
    fetchData();
  };

  // Open the create modal in edit mode, pre-filled with the event data
  const openEditModal = (event) => {
    setEditingEvent(event);
    setShowEventModal(false);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || 'cours',
      event_date: event.event_date || '',
      start_time: (event.start_time || '09:00').substring(0, 5),
      end_time: (event.end_time || '12:00').substring(0, 5),
      group_id: event.group_id ? event.group_id.toString() : '',
      student_id: event.student_id ? event.student_id.toString() : '',
      course_id: event.course_id ? event.course_id.toString() : '',
      trainer_id: event.trainer_id ? event.trainer_id.toString() : '',
      location: event.location || '',
      resources_url: event.resources_url || ''
    });
    setShowCreateModal(true);
  };

  // Handle create event
  const handleCreate = async () => {
    if (!formData.title || !formData.event_date || !formData.start_time || !formData.end_time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!formData.group_id && !formData.student_id) {
      toast.error('Veuillez sélectionner un groupe ou un étudiant');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        group_id: formData.group_id ? parseInt(formData.group_id) : null,
        student_id: formData.student_id ? parseInt(formData.student_id) : null,
        course_id: formData.course_id ? parseInt(formData.course_id) : null,
        trainer_id: formData.trainer_id ? parseInt(formData.trainer_id) : null
      };
      
      if (editingEvent) {
        await api.put(`/planning/events/${editingEvent.id}`, payload);
        toast.success('Événement mis à jour');
      } else {
        await api.post('/planning/events', payload);
        toast.success('Événement créé avec succès');
      }
      setShowCreateModal(false);
      setEditingEvent(null);
      fetchData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Handle batch create
  const handleBatchCreate = async () => {
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Veuillez remplir le titre et les horaires');
      return;
    }
    if (!formData.group_id && !formData.student_id) {
      toast.error('Veuillez sélectionner un groupe ou un étudiant');
      return;
    }
    if (batchDates.length === 0) {
      toast.error('Veuillez sélectionner au moins une date');
      return;
    }

    setSaving(true);
    try {
      await api.post('/planning/events/batch', {
        title: formData.title,
        description: formData.description,
        event_type: formData.event_type,
        dates: batchDates.sort(),
        start_time: formData.start_time,
        end_time: formData.end_time,
        group_id: formData.group_id ? parseInt(formData.group_id) : null,
        student_id: formData.student_id ? parseInt(formData.student_id) : null,
        course_id: formData.course_id ? parseInt(formData.course_id) : null,
        trainer_id: formData.trainer_id ? parseInt(formData.trainer_id) : null,
        location: formData.location || null,
      });
      toast.success(`${batchDates.length} événement(s) créé(s) avec succès`);
      setShowBatchModal(false);
      setBatchDates([]);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Erreur batch:', error);
      toast.error('Erreur lors de la création en lot');
    } finally {
      setSaving(false);
    }
  };

  const toggleBatchDate = (dateStr) => {
    setBatchDates(prev =>
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
    );
  };

  const generateBatchCalendar = () => {
    const year = batchYear;
    const month = batchMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (7 - endDate.getDay()) % 7);
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return { days, month, year };
  };

  // Open event detail modal
  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Handle delete event
  const handleDelete = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    
    try {
      await api.delete(`/planning/events/${eventId}`);
      toast.success('Événement supprimé');
      setShowEventModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle export PDF
  const handleExport = async () => {
    if (!exportId) {
      toast.error('Veuillez sélectionner un groupe ou un étudiant');
      return;
    }
    
    try {
      let endpoint = exportTarget === 'group' 
        ? `/planning/export/group/${exportId}/pdf`
        : `/planning/export/student/${exportId}/pdf`;
      
      const params = new URLSearchParams({ view: exportView, year: exportYear });
      if (exportView === 'month') params.set('month', exportMonth);
      endpoint += `?${params.toString()}`;
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `planning_${exportYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF généré avec succès');
      setShowExportModal(false);
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  // Format time
  const formatTime = (time) => time?.substring(0, 5) || '';

  // Get month/year title
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const calendarDays = generateCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-violet-600" />
              Planning Pédagogique
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Gérez le planning de formation pour vos groupes et étudiants
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowExportModal(true)}>
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button variant="outline" onClick={() => { resetForm(); setBatchDates([]); setShowBatchModal(true); }}>
              <CalendarIcon className="w-4 h-4 mr-2" />
              Planification rapide
            </Button>
            <Button onClick={() => openCreateModal()} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel événement
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filtres :</span>
              </div>
              
              <NativeSelect
                value={filterGroup}
                onChange={setFilterGroup}
                options={[
                  { value: 'all', label: 'Tous les groupes' },
                  ...groups.map(g => ({ value: g.id.toString(), label: g.name }))
                ]}
                className="w-48"
              />
              
              <NativeSelect
                value={filterType}
                onChange={setFilterType}
                options={[
                  { value: 'all', label: 'Tous les types' },
                  ...EVENT_TYPE_OPTIONS
                ]}
                className="w-40"
              />
              
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Aujourd'hui
                </Button>
                <Button variant="outline" size="sm" onClick={goToNextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xl text-center">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = day.getTime() === today.getTime();
                const dayEvents = getEventsForDay(day);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-24 p-1 border rounded-lg transition-colors cursor-pointer ${
                      isCurrentMonth 
                        ? 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700' 
                        : 'bg-slate-50 dark:bg-slate-900/50'
                    } ${isToday ? 'ring-2 ring-violet-500' : 'border-slate-200 dark:border-slate-700'}`}
                    onClick={() => openCreateModal(day)}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isToday 
                        ? 'text-violet-600' 
                        : isCurrentMonth 
                          ? 'text-slate-900 dark:text-white' 
                          : 'text-slate-400'
                    }`}>
                      {day.getDate()}
                    </div>
                    
                    {/* Events */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => {
                        const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.cours;
                        return (
                          <div
                            key={event.id}
                            onClick={(e) => { e.stopPropagation(); openEventModal(event); }}
                            className={`text-xs px-1.5 py-0.5 rounded truncate text-white ${typeConfig.color} cursor-pointer hover:opacity-80`}
                          >
                            {formatTime(event.start_time)} {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-slate-500 pl-1">
                          +{dayEvents.length - 3} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Légende :</span>
              {Object.entries(EVENT_TYPES).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${config.color}`}></div>
                  <span className="text-sm text-slate-600 dark:text-slate-400">{config.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingEvent ? <Edit className="w-5 h-5 text-violet-600" /> : <Plus className="w-5 h-5 text-violet-600" />}
              {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
            </DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Modifiez les détails de cet événement' : 'Créez un événement pour un groupe ou un étudiant'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Cours de Management - Module 1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type d'événement *</Label>
                <NativeSelect
                  value={formData.event_type}
                  onChange={(v) => setFormData({ ...formData, event_type: v })}
                  options={EVENT_TYPE_OPTIONS}
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Heure début *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Heure fin *</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-slate-700 dark:text-slate-300">Cible (groupe ou étudiant)</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-sm">Groupe</Label>
                  <NativeSelect
                    value={formData.group_id}
                    onChange={(v) => setFormData({ ...formData, group_id: v, student_id: '' })}
                    options={[
                      { value: '', label: 'Aucun groupe' },
                      ...groups.map(g => ({ value: g.id.toString(), label: g.name }))
                    ]}
                  />
                </div>
                <div>
                  <Label className="text-sm">Ou étudiant individuel</Label>
                  <NativeSelect
                    value={formData.student_id}
                    onChange={(v) => setFormData({ ...formData, student_id: v, group_id: '' })}
                    options={[
                      { value: '', label: 'Aucun étudiant' },
                      ...students.map(s => ({ value: s.id.toString(), label: `${s.first_name} ${s.last_name}` }))
                    ]}
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cours associé</Label>
                <NativeSelect
                  value={formData.course_id}
                  onChange={(v) => setFormData({ ...formData, course_id: v })}
                  options={[
                    { value: '', label: 'Aucun cours' },
                    ...courses.map(c => ({ value: c.id.toString(), label: c.name }))
                  ]}
                />
              </div>
              <div>
                <Label>Formateur</Label>
                <NativeSelect
                  value={formData.trainer_id}
                  onChange={(v) => setFormData({ ...formData, trainer_id: v })}
                  options={[
                    { value: '', label: 'Aucun formateur' },
                    ...trainers.map(t => ({ value: t.id.toString(), label: `${t.first_name} ${t.last_name}` }))
                  ]}
                />
              </div>
            </div>
            
            <div>
              <Label>Lieu / Lien</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Salle A101 ou https://meet.google.com/..."
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de l'événement..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingEvent(null); }}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : (editingEvent ? <Edit className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
              {editingEvent ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && EVENT_TYPES[selectedEvent.event_type] && (
                <div className={`w-3 h-3 rounded ${EVENT_TYPES[selectedEvent.event_type].color}`}></div>
              )}
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <CalendarIcon className="w-4 h-4" />
                {new Date(selectedEvent.event_date).toLocaleDateString('fr-FR', { 
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </div>
              
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                <Clock className="w-4 h-4" />
                {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
              </div>
              
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm text-white ${EVENT_TYPES[selectedEvent.event_type]?.color || 'bg-slate-500'}`}>
                  {selectedEvent.event_type_label}
                </span>
              </div>
              
              {selectedEvent.group_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Users className="w-4 h-4" />
                  Groupe : {selectedEvent.group_name}
                </div>
              )}
              
              {selectedEvent.student_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <GraduationCap className="w-4 h-4" />
                  Étudiant : {selectedEvent.student_name}
                </div>
              )}
              
              {selectedEvent.trainer_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <GraduationCap className="w-4 h-4" />
                  Formateur : {selectedEvent.trainer_name}
                </div>
              )}
              
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Building2 className="w-4 h-4" />
                  Lieu : {selectedEvent.location}
                </div>
              )}
              
              {selectedEvent.description && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {selectedEvent.description}
                  </p>
                </div>
              )}
              
              {/* Trainer enrichment */}
              {(selectedEvent.trainer_comment || selectedEvent.trainer_instructions) && (
                <div className="pt-2 border-t bg-violet-50 dark:bg-violet-900/20 p-3 rounded-lg">
                  <h4 className="font-medium text-violet-700 dark:text-violet-300 mb-2">
                    Notes du formateur
                  </h4>
                  {selectedEvent.trainer_comment && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                      {selectedEvent.trainer_comment}
                    </p>
                  )}
                  {selectedEvent.trainer_instructions && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <strong>Instructions :</strong> {selectedEvent.trainer_instructions}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleDelete(selectedEvent?.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
            <Button
              variant="outline"
              onClick={() => openEditModal(selectedEvent)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
            <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setShowEventModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Creation Modal */}
      <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-violet-600" />
              Planification rapide
            </DialogTitle>
            <DialogDescription>
              Cochez les dates puis remplissez les détails — un événement sera créé pour chaque date sélectionnée
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Recurrence generator */}
            <div className="border border-violet-200 dark:border-violet-800 rounded-lg p-3 bg-violet-50/50 dark:bg-violet-900/10">
              <Label className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                Récurrence (optionnel)
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                Générez automatiquement les dates, ex: tous les lundis entre deux dates
              </p>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div>
                  <Label className="text-xs">Du</Label>
                  <Input type="date" value={recurStart} onChange={(e) => setRecurStart(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Au</Label>
                  <Input type="date" value={recurEnd} onChange={(e) => setRecurEnd(e.target.value)} />
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {[{ wd: 1, label: 'Lun' }, { wd: 2, label: 'Mar' }, { wd: 3, label: 'Mer' }, { wd: 4, label: 'Jeu' }, { wd: 5, label: 'Ven' }, { wd: 6, label: 'Sam' }, { wd: 0, label: 'Dim' }].map(({ wd, label }) => (
                  <button
                    key={wd}
                    type="button"
                    onClick={() => toggleRecurWeekday(wd)}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                      recurWeekdays.includes(wd)
                        ? 'bg-violet-600 text-white border-violet-600'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-violet-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={generateRecurringDates} className="w-full">
                <CalendarIcon className="w-4 h-4 mr-2" />
                Générer les dates
              </Button>
            </div>

            {/* Month navigator */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => {
                const d = new Date(batchYear, batchMonth - 1, 1);
                setBatchMonth(d.getMonth());
                setBatchYear(d.getFullYear());
              }}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-sm">
                {monthNames[batchMonth]} {batchYear}
              </span>
              <Button variant="ghost" size="sm" onClick={() => {
                const d = new Date(batchYear, batchMonth + 1, 1);
                setBatchMonth(d.getMonth());
                setBatchYear(d.getFullYear());
              }}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Date selection grid */}
            {(() => {
              const { days, month: bm, year: by } = generateBatchCalendar();
              return (
                <div>
                  <div className="grid grid-cols-7 gap-0.5 text-center text-xs font-medium text-slate-500 mb-1">
                    {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => <div key={d}>{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5">
                    {days.map((day, i) => {
                      const isCurrentMonth = day.getMonth() === bm;
                      const dateStr = toLocalDateKey(day);
                      const isSelected = batchDates.includes(dateStr);
                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={!isCurrentMonth}
                          onClick={() => isCurrentMonth && toggleBatchDate(dateStr)}
                          className={`p-1.5 text-xs rounded transition-colors ${
                            !isCurrentMonth ? 'text-slate-300 cursor-default' :
                            isSelected ? 'bg-violet-600 text-white font-bold' :
                            'hover:bg-violet-100 text-slate-700'
                          }`}
                        >
                          {day.getDate()}
                        </button>
                      );
                    })}
                  </div>
                  {batchDates.length > 0 && (
                    <p className="text-xs text-violet-600 mt-2 font-medium">
                      {batchDates.length} date(s) sélectionnée(s)
                      <button type="button" onClick={() => setBatchDates([])} className="ml-2 text-red-500 hover:underline">Tout effacer</button>
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="border-t pt-3 space-y-3">
              <div>
                <Label>Titre *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Ex: Cours de Management" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type *</Label>
                  <NativeSelect value={formData.event_type} onChange={(v) => setFormData({...formData, event_type: v})} options={EVENT_TYPE_OPTIONS} />
                </div>
                <div>
                  <Label>Heure début *</Label>
                  <Input type="time" value={formData.start_time} onChange={(e) => setFormData({...formData, start_time: e.target.value})} />
                </div>
                <div>
                  <Label>Heure fin *</Label>
                  <Input type="time" value={formData.end_time} onChange={(e) => setFormData({...formData, end_time: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Groupe</Label>
                  <NativeSelect value={formData.group_id} onChange={(v) => setFormData({...formData, group_id: v, student_id: ''})}
                    options={[{ value: '', label: 'Aucun' }, ...groups.map(g => ({ value: g.id.toString(), label: g.name }))]} />
                </div>
                <div>
                  <Label>Ou étudiant</Label>
                  <NativeSelect value={formData.student_id} onChange={(v) => setFormData({...formData, student_id: v, group_id: ''})}
                    options={[{ value: '', label: 'Aucun' }, ...students.map(s => ({ value: s.id.toString(), label: `${s.first_name} ${s.last_name}` }))]} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cours</Label>
                  <NativeSelect value={formData.course_id} onChange={(v) => setFormData({...formData, course_id: v})}
                    options={[{ value: '', label: 'Aucun' }, ...courses.map(c => ({ value: c.id.toString(), label: c.name }))]} />
                </div>
                <div>
                  <Label>Formateur</Label>
                  <NativeSelect value={formData.trainer_id} onChange={(v) => setFormData({...formData, trainer_id: v})}
                    options={[{ value: '', label: 'Aucun' }, ...trainers.map(t => ({ value: t.id.toString(), label: `${t.first_name} ${t.last_name}` }))]} />
                </div>
              </div>
              <div>
                <Label>Lieu / Lien</Label>
                <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="Salle A101 ou lien visio" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchModal(false)}>Annuler</Button>
            <Button onClick={handleBatchCreate} disabled={saving || batchDates.length === 0} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer {batchDates.length} événement(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export PDF Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-violet-600" />
              Export PDF
            </DialogTitle>
            <DialogDescription>
              Générez un planning PDF pour un groupe ou un étudiant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Vue</Label>
              <NativeSelect
                value={exportView}
                onChange={setExportView}
                options={[
                  { value: 'year', label: 'Annuelle' },
                  { value: 'month', label: 'Mensuelle' }
                ]}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Année</Label>
                <NativeSelect
                  value={exportYear.toString()}
                  onChange={(v) => setExportYear(parseInt(v))}
                  options={[
                    { value: '2025', label: '2025' },
                    { value: '2026', label: '2026' },
                    { value: '2027', label: '2027' }
                  ]}
                />
              </div>
              {exportView === 'month' && (
                <div>
                  <Label>Mois</Label>
                  <NativeSelect
                    value={exportMonth.toString()}
                    onChange={(v) => setExportMonth(parseInt(v))}
                    options={monthNames.map((m, i) => ({ value: (i+1).toString(), label: m }))}
                  />
                </div>
              )}
            </div>
            
            <div>
              <Label>Type d'export</Label>
              <NativeSelect
                value={exportTarget}
                onChange={(v) => { setExportTarget(v); setExportId(''); }}
                options={[
                  { value: 'group', label: 'Par groupe' },
                  { value: 'student', label: 'Par étudiant' }
                ]}
              />
            </div>
            
            <div>
              <Label>{exportTarget === 'group' ? 'Groupe' : 'Étudiant'}</Label>
              <NativeSelect
                value={exportId}
                onChange={setExportId}
                options={[
                  { value: '', label: `Sélectionner un ${exportTarget === 'group' ? 'groupe' : 'étudiant'}` },
                  ...(exportTarget === 'group' 
                    ? groups.map(g => ({ value: g.id.toString(), label: g.name }))
                    : students.map(s => ({ value: s.id.toString(), label: `${s.first_name} ${s.last_name}` }))
                  )
                ]}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleExport} disabled={!exportId} className="bg-violet-600 hover:bg-violet-700">
              <Download className="w-4 h-4 mr-2" />
              Générer PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CfaAdminPlanningPage;
